/**
 * Scroll-driven motion primitives for the project landing pages.
 *
 * This is the single animation system for the public project story /
 * pitch pages (extends the `Reveal` that shipped with the interactive
 * pitches). Everything here is IntersectionObserver + CSS: no animation
 * library, no scroll-jank. Every primitive renders its final, static
 * state when the visitor prefers reduced motion or before the observer
 * is armed, so content is always visible without JavaScript and the
 * hero / LCP element is never gated behind an effect.
 */
import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from "react";

const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function")
    return false;
  return window.matchMedia(REDUCED_MOTION_QUERY).matches;
}

export function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => prefersReducedMotion());
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return;
    const mql = window.matchMedia(REDUCED_MOTION_QUERY);
    const onChange = () => setReduced(mql.matches);
    onChange();
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);
  return reduced;
}

/**
 * Observe a node once; resolves `true` the first time it intersects.
 * Returns `armed` (observer attached — safe to apply hidden styles) and
 * `visible`. Reduced-motion users are never armed, so they see the
 * final state immediately.
 */
export function useRevealOnce<T extends Element>(
  options: { threshold?: number; rootMargin?: string } = {},
) {
  const { threshold = 0.14, rootMargin = "0px 0px -8% 0px" } = options;
  const ref = useRef<T | null>(null);
  const [armed, setArmed] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setVisible(true);
        observer.unobserve(node);
      },
      { threshold, rootMargin },
    );
    observer.observe(node);
    setArmed(true);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return { ref, armed, visible };
}

type RevealVariant = "up" | "fade" | "scale" | "left" | "right";

const HIDDEN_TRANSFORM: Record<RevealVariant, string> = {
  up: "translate3d(0, 28px, 0)",
  fade: "translate3d(0, 0, 0)",
  scale: "translate3d(0, 0, 0) scale(0.965)",
  left: "translate3d(-28px, 0, 0)",
  right: "translate3d(28px, 0, 0)",
};

/**
 * Fade/slide a block into view the first time it enters the viewport.
 * Use `delay` (ms) to stagger siblings — e.g. `index * 90`.
 */
export function Reveal({
  children,
  className = "",
  delay = 0,
  variant = "up",
  duration = 800,
  threshold,
  style,
  testId,
}: {
  children: ReactNode;
  className?: string;
  delay?: number;
  variant?: RevealVariant;
  duration?: number;
  threshold?: number;
  style?: CSSProperties;
  testId?: string;
}) {
  const { ref, armed, visible } = useRevealOnce<HTMLDivElement>({ threshold });
  const motionStyle: CSSProperties | undefined = armed
    ? {
        opacity: visible ? 1 : 0,
        transform: visible
          ? "translate3d(0, 0, 0) scale(1)"
          : HIDDEN_TRANSFORM[variant],
        transition: `opacity ${duration}ms cubic-bezier(.2,.7,.2,1) ${delay}ms, transform ${duration}ms cubic-bezier(.2,.7,.2,1) ${delay}ms`,
        willChange: visible ? "auto" : "opacity, transform",
      }
    : undefined;

  return (
    <div
      ref={ref}
      className={`${className} motion-reduce:!transform-none motion-reduce:!opacity-100`}
      style={motionStyle ? { ...style, ...motionStyle } : style}
      data-reveal-state={armed ? (visible ? "visible" : "hidden") : "static"}
      data-testid={testId}
    >
      {children}
    </div>
  );
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/**
 * Count a number up from zero when it scrolls into view. Renders the
 * final formatted value for reduced-motion users and before the
 * observer is armed, so the figure is always correct in static HTML.
 */
export function CountUp({
  value,
  format = (n: number) => Math.round(n).toLocaleString("en-US"),
  duration = 1400,
  className,
  testId,
}: {
  value: number;
  format?: (n: number) => string;
  duration?: number;
  className?: string;
  testId?: string;
}) {
  const { ref, armed, visible } = useRevealOnce<HTMLSpanElement>({
    threshold: 0.4,
    rootMargin: "0px",
  });
  const [progress, setProgress] = useState(1);

  useEffect(() => {
    if (!armed) return;
    if (!visible) {
      setProgress(0);
      return;
    }
    if (typeof requestAnimationFrame !== "function") {
      setProgress(1);
      return;
    }
    let frame = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      setProgress(easeOutCubic(t));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [armed, visible, duration]);

  const shown = armed ? value * progress : value;
  return (
    <span
      ref={ref}
      className={className}
      data-testid={testId}
      data-count-state={armed ? (visible ? "counting" : "waiting") : "static"}
    >
      {format(shown)}
    </span>
  );
}

/**
 * Subtle vertical parallax for section imagery. The child drifts
 * `speed` × its distance from the viewport centre, clamped to ±`max`
 * px. Only listens to scroll while the node is on screen, and is a
 * no-op for reduced motion.
 */
export function Parallax({
  children,
  className = "",
  speed = 0.1,
  max = 48,
}: {
  children: ReactNode;
  className?: string;
  speed?: number;
  max?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const node = ref.current;
    if (!node || typeof IntersectionObserver === "undefined") return;

    let frame = 0;
    let listening = false;
    const update = () => {
      frame = 0;
      const rect = node.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const centre = rect.top + rect.height / 2 - vh / 2;
      const next = Math.max(-max, Math.min(max, -centre * speed));
      setOffset(Math.round(next * 10) / 10);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const start = () => {
      if (listening) return;
      listening = true;
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll);
      onScroll();
    };
    const stop = () => {
      if (!listening) return;
      listening = false;
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    };
    const observer = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? start() : stop()),
      { rootMargin: "20% 0px 20% 0px" },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      stop();
    };
  }, [speed, max]);

  return (
    <div
      ref={ref}
      className={`${className} motion-reduce:!transform-none`}
      style={
        offset
          ? {
              transform: `translate3d(0, ${offset}px, 0)`,
              willChange: "transform",
            }
          : undefined
      }
    >
      {children}
    </div>
  );
}

/**
 * Scroll progress (0–1) through a tall runway while a sticky child stays
 * pinned. Used by the feature-film stage — prefer this over scroll
 * libraries. Reduced-motion callers should skip the tall runway and
 * ignore progress.
 */
export function useStickyScrollProgress(
  options: { disabled?: boolean } = {},
): {
  runwayRef: RefObject<HTMLElement | null>;
  progress: number;
} {
  const runwayRef = useRef<HTMLElement | null>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (options.disabled || prefersReducedMotion()) {
      setProgress(0);
      return;
    }
    const node = runwayRef.current;
    if (!node) return;

    let frame = 0;
    let listening = false;
    const update = () => {
      frame = 0;
      const rect = node.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const total = Math.max(1, rect.height - vh);
      const scrolled = Math.min(total, Math.max(0, -rect.top));
      setProgress(scrolled / total);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const start = () => {
      if (listening) return;
      listening = true;
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll);
      onScroll();
    };
    const stop = () => {
      if (!listening) return;
      listening = false;
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
      frame = 0;
    };

    if (typeof IntersectionObserver === "undefined") {
      start();
      return () => stop();
    }
    const observer = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? start() : stop()),
      { rootMargin: "10% 0px 10% 0px" },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      stop();
    };
  }, [options.disabled]);

  return { runwayRef, progress };
}

/**
 * Thin fixed progress bar along the top of the viewport reflecting how
 * far down the page the visitor has scrolled.
 */
export function ScrollProgress({
  className = "",
  color = "#D7B85A",
  testId = "project-scroll-progress",
}: {
  className?: string;
  color?: string;
  testId?: string;
}) {
  const [fraction, setFraction] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let frame = 0;
    const update = () => {
      frame = 0;
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      setFraction(max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);

  return (
    <div
      className={`pointer-events-none fixed inset-x-0 top-0 z-40 h-[3px] ${className}`}
      aria-hidden="true"
      data-testid={testId}
    >
      <div
        className="h-full origin-left motion-reduce:transition-none"
        style={{
          transform: `scaleX(${fraction})`,
          backgroundColor: color,
          transition: "transform 120ms linear",
          boxShadow: `0 0 12px ${color}99`,
        }}
      />
    </div>
  );
}
